/**
 * Meter geometry — 3 concentric rings with golden-ratio line-width
 * taper. Each inner-successor ring is widened by PHI; the outer ring
 * is therefore the thickest and the inner ring the thinnest.
 *
 * Numeric invariants (slim PHI ladder so rings read as a graceful
 * three-tier dial rather than a stout horseshoe at typical canvas sizes):
 *   base           = w * 0.04            (canvas-anchored — every thickness
 *                                          and the radii scale together)
 *   innerLineWidth = 0.85·base
 *   midLineWidth   = innerLineWidth · PHI   ≈ 1.375·base
 *   outerLineWidth = midLineWidth · PHI     ≈ 2.225·base
 *   gap            = 0.5·base
 *   blurMargin     = 0.45·base   (reserved so shadow glow never clips)
 *   dialRingGap    = base / PHI
 *   dialLength     = innerRadius − innerLineWidth/2 − dialWidth/2 − dialRingGap
 *   dialWidth      = 0.56·base
 *   dotRadius      = 0.50·base
 *
 * The `0.04` coefficient is bit-exact with the prior `emToPixels("16px") * dpr`
 * formula at the historical 400-CSS-pixel meter size (16 / 400 = 0.04), so
 * existing geometry holds at the calibration point. When `--meter-size`
 * scales (U directive 7 — 1.25× bump), every term scales uniformly because
 * `base` is now anchored to canvas width rather than the document root's
 * inherited font-size.
 *
 * A `valid` flag guards against tiny canvases where innerRadius would
 * collapse to zero; callers bail early when `valid === false`.
 */

/** Inlined to avoid pulling a ~108 KB value-unit dependency for one line. */
function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

export const PHI = 1.618033988749;

/**
 * Outer-ring diameter as a fraction of the canvas width `w`.
 *
 * The dial is painted into a square canvas; the outermost ring's
 * diameter is `2 · outerRadius`, and `outerRadius = w/2 −
 * outerLineWidth/2 − blurMargin`. Both subtrahends are fixed multiples
 * of `base = 0.04·w`, so the ratio is constant:
 *
 *   outerLineWidth = 0.85 · PHI² · base   (≈ 0.08901·w)
 *   blurMargin     = 0.45 · base          (= 0.018·w)
 *   outerRadius    = w·(0.5 − 0.044507 − 0.018) = 0.437493·w
 *   diameter       = 2 · outerRadius      ≈ 0.874987·w
 *
 * The under-meter phase-progress bar in `MeterColumn.vue` reads this
 * ratio so the bar's width equals the dial's actual painted diameter
 * rather than the full (wider) column. Derived here from the same
 * `base`-anchored geometry — change a ring coefficient and this
 * tracks. Kept in sync with the `.phase-progress` width calc by the
 * contract comment there.
 */
const _base = 0.04;
const _outerLineWidth = 0.85 * PHI * PHI * _base;
const _blurMargin = 0.45 * _base;
export const DIAL_DIAMETER_RATIO = 2 * (0.5 - _outerLineWidth / 2 - _blurMargin);

/**
 * Per-metric scale config drives the live needle and the post-complete
 * dial-hold. The renderer maps a raw value to a 0..1 progress fraction
 * via {@link metricProgress}; value.js `lerp(startAngle, endAngle, t)` then
 * yields the needle angle (AV.W11b moved the interpolation onto value.js —
 * range-first arg order). Linear scales suit small-integer ms metrics; log10
 * scales spread the 50–1500 Mbps band so a 730 Mbps download doesn't peg
 * the needle at endAngle (the deployed-screenshot regression case).
 *
 * Peaks chosen against typical residential broadband — see
 * docs/tranches/R/audit/HARDENING-FRONTEND-DESIGN.md §4-revised.
 */
export type MetricKey = "ping" | "jitter" | "download" | "upload";

export interface MetricScale {
    min: number;
    peak: number;
    scale: "linear" | "log";
}

export const metricScales: Record<MetricKey, MetricScale> = {
    ping:     { min: 0, peak: 200,  scale: "linear" }, // 5–80 ms typical; 200 ms outlier ceiling
    jitter:   { min: 0, peak: 50,   scale: "linear" }, // 0.5–10 ms typical; 50 ms noisy-link ceiling
    download: { min: 0, peak: 1000, scale: "log" },    // 50–1500 Mbps band; gigabit-fibre soft ceiling
    upload:   { min: 0, peak: 500,  scale: "log" },    // 20–500 Mbps; symmetric gigabit rarer
};

/**
 * KEEP-with-DDR (AV.W11b suffusion — KEEP #2 of 3; see
 * docs/decisions/DDR-AV-W11B-SUFFUSION.md "Timing lane"). This is a DOMAIN
 * value→progress mapping (a per-metric linear / log10 scale against
 * broadband-calibrated peaks), NOT animation timing and NOT scalar
 * interpolation: value.js owns `lerp`/`scale`/`logerp`, but the choice of
 * WHICH scale + WHICH peak per metric is speedtest domain knowledge that no
 * animation/value library should own. It correctly stays local. (The single
 * `lerp` of the result onto an angle IS value.js — at the call sites.)
 *
 * Map a raw metric value to a 0..1 progress fraction. Floors zero and
 * negatives at 0, clamps overshoot at 1 — never returns NaN, Infinity,
 * or out-of-range t.
 */
export function metricProgress(metric: MetricKey, value: number): number {
    const cfg = metricScales[metric];
    if (cfg.scale === "linear") {
        return clamp((value - cfg.min) / (cfg.peak - cfg.min), 0, 1);
    }
    // log10 from a floor of 1 — avoids log(0)/log(<1)<0 and parks tiny
    // values at start.
    const v = Math.max(value, 1);
    return clamp(Math.log10(v) / Math.log10(cfg.peak), 0, 1);
}

export interface MeterConfig {
    startAngle: number;
    endAngle: number;

    // Three rings — golden-ratio taper
    outerLineWidth: number;
    midLineWidth: number;
    innerLineWidth: number;
    gap: number;
    blurMargin: number;
    outerRadius: number;
    midRadius: number;
    innerRadius: number;

    // Dial + center dot
    dialLength: number;
    dialWidth: number;
    dialRingGap: number;
    dotRadius: number;

    // Canvas metrics
    canvasWidth: number;
    canvasHeight: number;
    originX: number;
    originY: number;
    dpr: number;
    base: number;

    // Geometry validity — false when ring radii collapse to ≤0
    valid: boolean;
}

export function computeMeterConfig(
    cssWidth: number,
    cssHeight: number,
    dpr: number,
): MeterConfig {
    const startAngle = Math.PI * 0.8;
    const endAngle = 2 * Math.PI * 1.1;

    const w = cssWidth * dpr;
    const h = cssHeight * dpr;
    // Canvas-anchored scale knob — every thickness term below derives from
    // `base`, so a `--meter-size` bump scales rings + dial proportionally.
    // The 0.04 coefficient is bit-exact with the prior
    // `emToPixels("16px") * dpr` formula at 400-CSS-px (16 / 400 = 0.04).
    const base = w * 0.04;

    // Golden-ratio ladder (slim base): inner thinnest → outer thickest.
    // Slimmer than the prior 1.035·base inner so the radial proportions
    // read as breathing rings, not stout horseshoes, at typical viewport-
    // capped meter sizes.
    const innerLineWidth = 0.85 * base;
    const midLineWidth = innerLineWidth * PHI;
    const outerLineWidth = midLineWidth * PHI;
    const gap = base * 0.5;
    const blurMargin = base * 0.45;

    const outerRadius = w / 2 - outerLineWidth / 2 - blurMargin;
    const midRadius =
        outerRadius - outerLineWidth / 2 - gap - midLineWidth / 2;
    const innerRadius =
        midRadius - midLineWidth / 2 - gap - innerLineWidth / 2;

    const dialWidth = base * 0.56;
    const dialRingGap = base / PHI;
    // Canvas round caps extend past the line endpoint by dialWidth/2, so
    // subtract that cap plus a golden-ratio gap from the inner ring edge.
    const dialLength = innerRadius - innerLineWidth / 2 - dialWidth / 2 - dialRingGap;
    const dotRadius = base * 0.5;

    return {
        startAngle,
        endAngle,
        outerLineWidth,
        midLineWidth,
        innerLineWidth,
        gap,
        blurMargin,
        outerRadius,
        midRadius,
        innerRadius,
        dialLength,
        dialWidth,
        dialRingGap,
        dotRadius,
        canvasWidth: w,
        canvasHeight: h,
        originX: w / 2,
        originY: h / 2,
        dpr,
        base,
        valid: innerRadius > base * 0.5 && outerRadius > 0,
    };
}
